import React, { Dispatch, SetStateAction } from 'react';

import { ApiErrors, QueryParams, QueryResult, ResponseMeta, useQuery } from '@betterment/js-runtime';
import _uniqBy from 'lodash/uniqBy';

import { DateFilter, toDateRange } from '../../Shared/date-filter';
import useDeepMemo from '../../Shared/hooks/useDeepMemo';
import { urlBuilder } from '../../Shared/urlBuilder';
import { DetailedTransaction, FormattedTransactionsFilterParams, UnformattedTransactionsFilterParams } from '../data';

export interface TransactionsContextType {
  transactions: DetailedTransaction[] | null;
  setTransactions: Dispatch<SetStateAction<DetailedTransaction[] | null>>;
}

export interface UseTransactionsResult {
  isLoading: boolean;
  errors: ApiErrors | null;
  canLoadMore: boolean;
  transactions: DetailedTransaction[] | null;
  page: number;
  totalPages: number;
  paginateTransactions: (params?: UnformattedTransactionsFilterParams) => void;
  loadData: QueryResult<DetailedTransaction[]>['refetch'];
  replaceTransaction: (transacton: DetailedTransaction) => void;
  markMatchingTransactionsAsDirty: (predicate: (transaction: DetailedTransaction) => boolean) => void;
}

export interface UseTransactionsOptions extends Partial<QueryParams> {
  skip?: boolean;
  filter?: UnformattedTransactionsFilterParams | null;
}

export const TransactionsContext = React.createContext<TransactionsContextType | null>(null);

export const TransactionsProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  /*
   * WARNING: current implementation forces all consumers of transactions to share the same list of data;
   *          beware rendering multiple consumers on the page at once as all filters/pagination are shared
   */
  const [transactions, setTransactions] = React.useState<DetailedTransaction[] | null>(null);
  const values = React.useMemo(() => ({ transactions, setTransactions }), [transactions, setTransactions]);
  return <TransactionsContext.Provider value={values}>{children}</TransactionsContext.Provider>;
};

function formatFilterParams(
  params: UnformattedTransactionsFilterParams | undefined | null
): FormattedTransactionsFilterParams | null {
  if (!params) return null;

  let dateFilterParams: { startDate: string; endDate: string } | { startOn: string; endOn: string } | null;
  if (!params.dateFilter) {
    dateFilterParams = null;
  } else {
    dateFilterParams = toDateRange(params.dateFilter as DateFilter, params.from, params.to);
  }

  if (dateFilterParams) {
    dateFilterParams = {
      startDate: dateFilterParams.startOn,
      endDate: dateFilterParams.endOn,
    };
  }

  return {
    page: params.page,
    ...dateFilterParams,
    categories: params.categories,
  };
}

export const useTransactions = (
  accountId: number | null,
  // @ts-expect-error: js-runtime QueryParams time needs to be updated to allow for any value type
  options?: UseTransactionsOptions = { skip: false, filter: {} }
): UseTransactionsResult => {
  const ctx = React.useContext(TransactionsContext);
  if (!ctx) {
    throw new Error('[useTransactions()] Must use under a TransactionsProvider');
  }
  const { transactions, setTransactions } = ctx;

  const { skip, filter } = options;
  if (!accountId && !skip) {
    throw new Error('[useTransactions()] Must pass an accountId or skip=true option');
  }

  const [page, setPage] = React.useState<number>(0);
  const [totalPages, setTotalPages] = React.useState<number>(0);

  const formattedFilterParams = formatFilterParams(filter);

  const qs = useDeepMemo(() => formattedFilterParams, formattedFilterParams);
  React.useEffect(() => {
    if (qs) {
      setPage(0);
    }
  }, [qs]);

  const queryUrl = urlBuilder.apiV2CheckingDetailedTransactions.absolute({ accountId: accountId! });

  const {
    loading: isQueryLoading,
    errors,
    data,
    meta,
    refetch,
  } = useQuery<DetailedTransaction[], ResponseMeta>(queryUrl, {
    skip,
    // @ts-expect-error js-runtime QueryParams time needs to be updated to allow for any value type
    qs,
  });

  const isLoading = isQueryLoading || !data;

  const tp = meta ? meta.totalPages : totalPages;
  React.useEffect(() => {
    setTotalPages(tp);
  }, [tp]);

  React.useEffect(() => {
    if (!data) {
      return;
    }
    setTransactions(existingTransactions => {
      if (page === 0) {
        return data;
      }
      if (existingTransactions) {
        return _uniqBy(existingTransactions.concat(data), 'id');
      }
      return existingTransactions;
    });
  }, [data, page, setTransactions]);

  const paginateTransactions = React.useCallback(
    async (params: UnformattedTransactionsFilterParams = {}) => {
      const nextPage = page + 1;
      const formattedParams = formatFilterParams(params);

      setPage(nextPage);
      const { data: newData } = await refetch({ qs: { ...formattedParams, page: nextPage } });
      if (newData) {
        setTransactions(existingTransactions => {
          if (existingTransactions) {
            return _uniqBy(existingTransactions.concat(newData), 'id');
          }
          return null;
        });
      }
    },
    [page, refetch, setTransactions]
  );

  const replaceTransaction = React.useCallback(
    (newTransaction: DetailedTransaction) => {
      setTransactions(currentTransactions => {
        if (!currentTransactions) return null;

        return currentTransactions.map(transaction =>
          newTransaction.id === transaction.id ? newTransaction : transaction
        );
      });
    },
    [setTransactions]
  );

  const markMatchingTransactionsAsDirty: UseTransactionsResult['markMatchingTransactionsAsDirty'] = React.useCallback(
    predicate => {
      setTransactions(currentTransactions => {
        if (!currentTransactions) return null;

        return currentTransactions.map(transaction =>
          predicate(transaction) ? { ...transaction, isDirty: true } : transaction
        );
      });
    },
    [setTransactions]
  );

  return {
    canLoadMore: page + 1 < totalPages,
    errors,
    transactions,
    isLoading,
    loadData: refetch,
    page,
    totalPages,
    paginateTransactions,
    replaceTransaction,
    markMatchingTransactionsAsDirty,
  };
};
